import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  Store as StoreIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ExhibitorMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface ExhibitorMetricsSectionProps {
  data: ExhibitorMetrics;
  loading?: boolean;
}

const ExhibitorMetricsSection: React.FC<ExhibitorMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const verificationData = useMemo(
    () => [
      {
        name: 'Verified',
        value: data.verifiedExhibitors,
        color: CHART_COLORS.success,
      },
      {
        name: 'Unverified',
        value: data.unverifiedExhibitors,
        color: CHART_COLORS.warning,
      },
    ],
    [data.verifiedExhibitors, data.unverifiedExhibitors]
  );

  const companyTypeData = useMemo(
    () => [
      {
        name: 'Startups',
        value: data.startupCompanies,
        color: CHART_COLORS.purple,
      },
      {
        name: 'Established',
        value: data.establishedCompanies,
        color: CHART_COLORS.primary,
      },
    ],
    [data.startupCompanies, data.establishedCompanies]
  );

  const boothTypeData = useMemo(
    () =>
      data.boothTypeDistribution.map((item, index) => ({
        name: item.boothType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.boothTypeDistribution]
  );

  const categoryData = useMemo(
    () =>
      data.exhibitorsByCategory.slice(0, 6).map((item, index) => ({
        name: item.categoryName,
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.exhibitorsByCategory]
  );

  const totalExhibitors = data.verifiedExhibitors + data.unverifiedExhibitors;
  const hasVerificationData = totalExhibitors > 0;
  const hasCompanyTypeData = data.startupCompanies + data.establishedCompanies > 0;

  return (
    <Box>
      <SectionHeader title="Exhibitors" icon={<StoreIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Verified Exhibitors"
            value={data.verifiedExhibitors}
            icon={<VerifiedIcon />}
            color={CHART_COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Unverified Exhibitors"
            value={data.unverifiedExhibitors}
            icon={<StoreIcon />}
            color={CHART_COLORS.warning}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Featured Exhibitors"
            value={data.featuredExhibitors}
            icon={<StarIcon />}
            color={CHART_COLORS.purple}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Exhibitors"
            value={totalExhibitors}
            icon={<BusinessIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>

        {/* Charts Row 1 */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Verification Status"
            subtitle="Verified vs unverified exhibitors"
            height={280}
            loading={loading}
          >
            {!hasVerificationData ? (
              <EmptyState message="No verification data available" height={250} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={verificationData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {verificationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 3,
                  }}
                >
                  {verificationData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Company Type"
            subtitle="Startups vs established companies"
            height={280}
            loading={loading}
          >
            {!hasCompanyTypeData ? (
              <EmptyState message="No company type data available" height={250} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={companyTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {companyTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 3,
                  }}
                >
                  {companyTypeData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        {/* Charts Row 2 */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Booth Type Distribution"
            subtitle="Distribution by booth type"
            height={280}
            loading={loading}
          >
            {boothTypeData.length === 0 ? (
              <EmptyState message="No booth type data available" height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={boothTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    tick={{ fontSize: 11 }}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Exhibitors by Category"
            subtitle="Top categories"
            height={280}
            loading={loading}
          >
            {categoryData.length === 0 ? (
              <EmptyState
                message="No category data available"
                icon={<CategoryIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={categoryData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="#6b7280"
                    width={120}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExhibitorMetricsSection;
